import {useMemo} from 'react';

import {
  SemanticLegendByMetric,
  useMetricStatisticsAggregationQueryQuery,
  useMetricStatisticsQueryQuery,
  useSemanticLegendsQueryQuery,
} from '../../../../../generated/graphql';
import {EMPTY_FILTERS, toMongo} from '../../../../../util/filters';
import {safeLocalStorage} from '../../../../../util/localStorage';
import {useRampFlagSemanticLegend} from '../../../../../util/rampFeatureFlags';
import {SemanticLegendSettings, YAxisValues} from '../../types';
import {DEFAULT_SEMANTIC_LEGEND_SETTINGS} from '../SemanticLegendDefaults';
import {parseBucketsFromSemanticLegendByMetric} from './parseBuckets';

export const useSemanticLegendSettingsQueryProps = (props: {
  entityName: string;
  projectName: string;
  viewID: string;
  filters: string;
  viewName: string;
  updateSemanticLegendSettings: (
    settings: Partial<SemanticLegendSettings>
  ) => void;
  createSemanticLegendSettings: (
    settings: Partial<SemanticLegendSettings>
  ) => void;
}) => {
  const isSemanticLegendEnabled = useRampFlagSemanticLegend(props.entityName);
  const {viewID, viewName, createSemanticLegendSettings} = props;

  const {
    data: semanticLegendSettings,
    error: semanticLegendSettingsError,
    loading: semanticLegendSettingsLoading,
  } = useSemanticLegendsQueryQuery({
    variables: {id: viewID ?? ''},
    skip: viewID === '' || !isSemanticLegendEnabled,
  });

  // Get the aggregates for the current metric
  const currentMetric =
    semanticLegendSettings?.view?.semanticLegend?.currentMetric ?? 'default';

  const shouldSkipMetricStatistics =
    !currentMetric ||
    currentMetric === '' ||
    currentMetric === 'default' ||
    !isSemanticLegendEnabled;
  const {
    data: metricStatistics,
    error: metricStatisticsError,
    loading: metricStatisticsLoading,
  } = useMetricStatisticsAggregationQueryQuery({
    variables: {
      filters: JSON.stringify(toMongo(EMPTY_FILTERS)),
      limit: 10000,
      order: 'desc',
      metricName: currentMetric,
      entityName: props.entityName,
      projectName: props.projectName,
    },
    skip: shouldSkipMetricStatistics,
    onCompleted: data => {
      const semanticLegendUpdate = constructSemanticLegendData(
        semanticLegendSettings?.view?.semanticLegend?.currentMetric ?? '',
        semanticLegendSettings?.view?.semanticLegend
          ?.semanticLegendsByMetric as SemanticLegendByMetric[]
      );
      props.updateSemanticLegendSettings({
        ...semanticLegendUpdate,
        metricStatistics: data.project?.runs?.metricStatisticsAggregation ?? {},
        viewID: viewID ?? '',
        shouldRecalculateBuckets: true,
      });
    },
  });

  // Get the metric statistics for each run
  const {
    data: metricStatisticsForRun,
    error: metricStatisticsForRunError,
    loading: metricStatisticsForRunLoading,
  } = useMetricStatisticsQueryQuery({
    context: {
      propagateErrors: true,
    },
    variables: {
      filters: props.filters,
      limit: 100,
      order: 'desc',
      metricName: currentMetric,
      entityName: props.entityName,
      projectName: props.projectName,
    },
    skip: shouldSkipMetricStatistics,
  });

  // Retrieve the semantic legend settings
  const {data: semanticLegendSettingsData} = useMemo(() => {
    // If a valid semantic legend settings object exists, use it
    if (semanticLegendSettings?.view?.semanticLegend?.currentMetric) {
      // Get the semantic legend settings for the current metric
      const semanticLegendUpdate = constructSemanticLegendData(
        semanticLegendSettings?.view?.semanticLegend?.currentMetric ?? '',
        semanticLegendSettings?.view?.semanticLegend
          ?.semanticLegendsByMetric as SemanticLegendByMetric[]
      );

      return {
        data: {
          metricStatistics:
            metricStatistics?.project?.runs?.metricStatisticsAggregation ?? {},
          metricStatisticsForRun:
            metricStatisticsForRun?.project?.runs?.metricStatistics ?? [],
          ...semanticLegendUpdate,
          viewID: viewID ?? '',
        },
        loading:
          semanticLegendSettingsLoading ||
          metricStatisticsLoading ||
          metricStatisticsForRunLoading,
        error:
          semanticLegendSettingsError ||
          metricStatisticsError ||
          metricStatisticsForRunError,
      };
    }
    // if no semantic legend settings object exists, check local storage for a saved semantic legend settings object
    const semanticLegendFromStorage = safeLocalStorage.getItem(
      `semanticLegendSettings-${viewName}`
    );
    if (semanticLegendFromStorage) {
      const parsedSemanticLegendFromStorage = {
        ...JSON.parse(semanticLegendFromStorage),
        viewID: viewID ?? '',
      };
      // if the view id is not empty and we're not waiting for any new data to load,
      // create the semantic legend settings from the stored object in local storage for the current view
      if (viewID && !semanticLegendSettingsLoading) {
        createSemanticLegendSettings(parsedSemanticLegendFromStorage);
        // remove the saved semantic legend settings from local storage because it's now stored in the backend
        safeLocalStorage.removeItem(`semanticLegendSettings-${viewName}`);
      }
      return {
        data: {
          ...parsedSemanticLegendFromStorage,
        },
        loading:
          semanticLegendSettingsLoading ||
          metricStatisticsLoading ||
          metricStatisticsForRunLoading,
        error:
          semanticLegendSettingsError ||
          metricStatisticsError ||
          metricStatisticsForRunError,
      };
    }

    // default to the default semantic legend settings
    return {
      data: {
        ...DEFAULT_SEMANTIC_LEGEND_SETTINGS,
        viewID: viewID ?? '',
      },
      loading:
        semanticLegendSettingsLoading ||
        metricStatisticsLoading ||
        metricStatisticsForRunLoading,
      error:
        semanticLegendSettingsError ||
        metricStatisticsError ||
        metricStatisticsForRunError,
    };
  }, [
    semanticLegendSettings,
    metricStatistics,
    semanticLegendSettingsLoading,
    metricStatisticsLoading,
    semanticLegendSettingsError,
    metricStatisticsError,
    metricStatisticsForRun,
    metricStatisticsForRunLoading,
    metricStatisticsForRunError,
    viewID,
    viewName,
    createSemanticLegendSettings,
  ]);

  return {
    semanticLegendSettingsData,
    semanticLegendSettingsLoading,
    semanticLegendSettingsError,
  };
};

function constructSemanticLegendData(
  currentMetric: string,
  semanticLegendsByMetric?: SemanticLegendByMetric[]
) {
  if (!semanticLegendsByMetric) {
    return DEFAULT_SEMANTIC_LEGEND_SETTINGS;
  }

  const currentLegend = semanticLegendsByMetric?.find(
    legend => legend?.metricName === currentMetric
  );
  if (currentLegend) {
    const {buckets, bucketCustomizations} =
      parseBucketsFromSemanticLegendByMetric(currentLegend);
    return {
      currentMetric: currentMetric ?? '',
      metricName: currentLegend?.metricName ?? '',
      metric: currentLegend?.metricName ?? '',
      numberOfBuckets: buckets.length ?? 0,
      buckets: buckets,
      bucketCustomizations: bucketCustomizations,
      excludedRunColor: currentLegend?.excludedRunColor ?? '',
      yAxisValue: (currentLegend?.yValue as YAxisValues) ?? YAxisValues.Min,
    };
  }
  return DEFAULT_SEMANTIC_LEGEND_SETTINGS;
}
